import { GhostButton, GridItem, Text } from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { useField } from "formik";
import { useContext } from "react";
import { Trans } from "react-i18next";

import { EducationList } from "./EducationList";
import { EducationModal } from "./EducationModal";
import { Education } from "./type";
import { sortHighestThenByEndDate } from "./utils";
import { ApplicationContext } from "../../Application.context";

type EducationCheckField = components["schemas"]["EducationCheckField"];

interface Props {
  field: EducationCheckField;
}

export function EducationInputField(props: Props) {
  const { saveData } = useContext(ApplicationContext);
  const [field, , helpers] = useField<EducationCheckField["value"]>(
    props.field.name
  );

  const educationQualifications = field.value.map((a, index) => ({
    ...a,
    index,
  }));

  const handleSaveEducation = (education: Education) => {
    let newEducationQualifications = [...educationQualifications];
    newEducationQualifications[education.index] = education;
    newEducationQualifications = sortHighestThenByEndDate(
      newEducationQualifications
    );
    saveEntries(newEducationQualifications);
  };

  const handleDeleteEducation = (index: number) => {
    let newEducationQualifications = [...educationQualifications].filter(
      (education) => index !== education.index
    );
    newEducationQualifications = sortHighestThenByEndDate(
      newEducationQualifications
    );
    saveEntries(newEducationQualifications);
  };

  const saveEntries = async (
    entries: components["schemas"]["EducationCheckFieldValue"][]
  ) => {
    await helpers.setValue(entries); // await required for race condition
    await saveData({ skipValidation: true, hasMultipleEntries: true });
  };

  const hasHighestEducation = educationQualifications.find(
    ({ highest_level }) => highest_level
  );

  return (
    <>
      <GridItem>
        <Text style="form/section-heading">
          <Trans>My Education</Trans>
        </Text>
      </GridItem>

      {educationQualifications.length > 0 && (
        <EducationList
          testID="Education-input-field-Education-list"
          educationQualifications={educationQualifications}
          onSave={handleSaveEducation}
          onDelete={handleDeleteEducation}
          requireHighestLevel={props.field.require_highest_level}
        />
      )}

      <GridItem className={css({ display: "block" })}>
        <EducationModal
          index={educationQualifications.length}
          allowHighest={
            props.field.require_highest_level && !hasHighestEducation
          }
          onSave={handleSaveEducation}
          trigger={
            <GhostButton
              testID="education-input-field-education-modal-button"
              leftIcon="add"
              size="medium"
              fill="background-base"
              border="border-strong"
            >
              <Trans>Add education</Trans>
            </GhostButton>
          }
        />
      </GridItem>
    </>
  );
}
